import { useEffect, useState } from 'react';
import { useUser } from '@stackframe/react';
import { Save, BellRing, BellOff } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { ensurePushSubscription, unsubscribePush } from '../lib/webpush.ts';
import { useToast } from '../components/Toast.tsx';
import { Reveal, Stagger, StaggerItem } from '../components/motion.tsx';
import { eventLabels } from '@cdvi-edge/shared';
import './SettingsPage.css';

const VAPID_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export function SettingsPage() {
  const tenant = trpc.tenant.current.useQuery();
  const utils = trpc.useUtils();
  const toast = useToast();
  const rename = trpc.tenant.rename.useMutation({
    onSuccess: () => utils.tenant.current.invalidate(),
  });
  const prefs = trpc.notif.listPrefs.useQuery();
  const setPref = trpc.notif.setPref.useMutation({
    onSuccess: () => utils.notif.listPrefs.invalidate(),
  });
  const subscribe = trpc.notif.subscribe.useMutation();
  const unsubscribe = trpc.notif.unsubscribe.useMutation();
  const [name, setName] = useState('');
  const [pushState, setPushState] = useState<'unknown' | 'denied' | 'subscribed' | 'idle'>(
    'unknown',
  );
  const user = useUser();

  useEffect(() => {
    if (!('Notification' in window)) {
      setPushState('denied');
      return;
    }
    if (Notification.permission === 'denied') setPushState('denied');
    else if (Notification.permission === 'granted') {
      navigator.serviceWorker?.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushState(sub ? 'subscribed' : 'idle'))
        .catch(() => setPushState('idle'));
    } else setPushState('idle');
  }, []);

  async function onSubscribe() {
    if (!VAPID_KEY) {
      toast.error('Push unavailable', 'VITE_VAPID_PUBLIC_KEY is not set in apps/web/.env.');
      return;
    }
    const payload = await ensurePushSubscription(VAPID_KEY);
    if (!payload) {
      setPushState('denied');
      return;
    }
    await subscribe.mutateAsync(payload);
    setPushState('subscribed');
    toast.success('Push enabled', 'You will receive alerts on this device.');
  }

  async function onUnsubscribe() {
    const endpoint = await unsubscribePush();
    if (endpoint) await unsubscribe.mutateAsync({ endpoint });
    setPushState('idle');
    toast.info('Push disabled', 'No more alerts on this device.');
  }

  const canEditTenant = tenant.data?.role === 'owner' || tenant.data?.role === 'admin';

  return (
    <div className="stack settings-page">
      <Reveal>
        <header className="page-head">
          <span className="section-eyebrow">Account · Configuration</span>
          <h1>
            <span className="serif-accent">Settings</span>
          </h1>
          <p className="muted">Manage your tenant, device alerts, and profile.</p>
        </header>
      </Reveal>

      <Stagger className="stack">
        <StaggerItem>
          <div className="card">
            <div className="card-header">
              <h2>Tenant</h2>
              <span className="spacer" />
              {tenant.data?.role && <Badge tone="info">{tenant.data.role}</Badge>}
            </div>
            {tenant.data ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  await rename.mutateAsync({ name: name || tenant.data!.name });
                  setName('');
                  toast.success('Tenant renamed');
                }}
                className="row"
              >
                <input
                  defaultValue={tenant.data.name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tenant name"
                  disabled={!canEditTenant}
                />
                <button type="submit" disabled={rename.isPending || !canEditTenant}>
                  <Save size={14} />
                  Save
                </button>
              </form>
            ) : (
              <div className="dim">No active tenant.</div>
            )}
          </div>
        </StaggerItem>

        <StaggerItem>
          <div className="card">
            <div className="card-header">
              <BellRing size={14} />
              <h2>Push notifications</h2>
              <span className="spacer" />
              {pushState === 'subscribed' && <Badge tone="ok">subscribed</Badge>}
              {pushState === 'denied' && <Badge tone="danger">blocked</Badge>}
              {pushState === 'idle' && <Badge>idle</Badge>}
            </div>
            <p className="muted setting-note">
              Get instant push alerts on this device for the events you opt into below. Critical
              kinds (door forced, alarms, tamper, AC loss) are ON by default.
            </p>
            <div className="row" style={{ marginTop: 8 }}>
              {pushState !== 'subscribed' ? (
                <button onClick={() => void onSubscribe()} disabled={pushState === 'denied'}>
                  <BellRing size={14} />
                  Enable on this device
                </button>
              ) : (
                <button className="secondary" onClick={() => void onUnsubscribe()}>
                  <BellOff size={14} />
                  Disable on this device
                </button>
              )}
            </div>
            {prefs.data && (
              <div className="prefs-scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th style={{ width: 90 }}>Push</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prefs.data.map((p) => (
                      <tr key={p.eventKind}>
                        <td>{eventLabels[p.eventKind] ?? p.eventKind}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={p.push}
                            aria-label={`Push for ${eventLabels[p.eventKind] ?? p.eventKind}`}
                            onChange={(e) =>
                              void setPref.mutate({
                                eventKind: p.eventKind,
                                push: e.target.checked,
                              })
                            }
                            style={{ width: 'auto' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </StaggerItem>

        <StaggerItem>
          <div className="card">
            <div className="card-header">
              <h2>You</h2>
            </div>
            <div className="muted">
              Signed in as <strong className="gold">{user?.primaryEmail ?? user?.displayName}</strong>
              . Use the avatar in the top bar to sign out or manage your Stack Auth profile.
            </div>
          </div>
        </StaggerItem>
      </Stagger>
    </div>
  );
}
